import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '../routing';
import { categoryLinesApi, formatCurrency, formatCurrencyWhole } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../utils/useIsMobile';
import { periodNoun, periodKeyLabel } from '../utils/targetPeriodLabels';
import { formatIsoRangeShort } from '../utils/dateFormat';
import { STATUS_COLOR, STATUS_TEXT_COLOR, TRACK_BG, barGeometry, statusLabel } from '../utils/targetBar';
import { categoryLineColor } from '../utils/classificationColors';
import { CHIP_ROW, CHIP, NOTE, MENU_SECTION, MENU_HEADING } from '../utils/cardMenu';
import Icon from './Icon';
import ComingSoonSheet from './ComingSoonSheet';
import AmberCallout from './AmberCallout';
import ChartTooltip, { useHoverTip } from './ChartTooltip';
import { track, EVENTS } from '../utils/analytics';

// פירוט שבועי מול יעדי תקציב — the budget card on /analysis. It replaced רכש
// מול יעדי תקציב (RestaurantTargetPanel), which blended every period the picked
// range touches into one ratio, after an A/B test; this one keeps the periods
// apart, so a month that looks "fine" on average doesn't hide the one week
// that wasn't.
//
// A category's LATEST period is the headline (large, status-coloured, the
// shared targetBar.js geometry) — the app's own convention for "what matters
// right now". Earlier periods collapse into a small trend strip: one bar per
// period, height by how close it got to target, green/red for a finished
// verdict (never amber — a finished period has no more pace to predict) and
// a flat gray dash for no target. ONE card-level tab bar (not one control per
// category) swaps to "שאר ה..." — every category's exact dates and ₪ at once,
// grouped by period — since 2-4 independent toggles read as busier than the
// data itself, and a lone text link above them went unnoticed entirely.
//
// No isMobile branch: every figure here already fits at phone width, so
// mobile and desktop render identically. (AmberCallout, used for unassigned
// spend, handles its own phone layout.)

const MUTED = '#8a94a3';
// The רכש/יעד line under each bar. It was 11px/#7c8592 and reported as
// "too small, can barely see it" — at 13px/#475569 it is the same slate the
// card's other supporting copy uses, and it is the only place the actual ₪
// behind a percentage is stated, so it has to survive a glance on a phone.
const CAPTION = '#475569';
// Every ₪ on this card is ex-VAT, and the basis rides right after each
// category's רכש figure (owner, 2026-09-21) rather than once in a header:
// this card has no header subtitle to carry it, and the retired card's
// "stated once" rule left the only mention inside the ⋮ note.
const EX_VAT = '(ללא מע״מ)';
// Same 10x10 swatch הכנסות מול הוצאות's category dot uses — one size for
// "this dot means category color" across every card on /analysis.
const ROW_DOT = { width: 10, height: 10, borderRadius: 2, flex: '0 0 auto', boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.15)' };
const NEUTRAL_BAR = { display: 'block', background: '#f1f5f9', borderRadius: 4, height: 15, overflow: 'hidden', marginBottom: 6 };
// Same 15px/500/#1a1a2e הכנסות מול הוצאות's own .cat-name uses for a category
// name — one type scale for "this is a category label" across every card on
// the page, not a smaller one on this card specifically.
const CAT_NAME = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15, fontWeight: 500, color: '#1a1a2e' };
// How many period blocks the "שאר ה..." tab paints before "הצג עוד" — 4 keeps
// the newest prior period (the one anyone actually checks) above the fold at
// 3 categories, which is the whole point of paging it.
const PERIOD_PAGE = 4;
// The "שאר ה..." tab's table: a fixed label column with every figure right up
// against it. A plain space-between row was wrong here, since that strands
// the number at the far edge on anything wider than a phone.
const HIST_GRID = { display: 'grid', gridTemplateColumns: '104px 1fr', columnGap: 10, alignItems: 'baseline' };
// The trend strip's vertical scale. TICK_CAP is where the height stops growing
// (a 3000% period would otherwise dwarf every other tick into invisibility).
const STRIP_H = 14;
const TICK_MIN = 4;
const TICK_SPAN = 10;
const TICK_CAP = 150;
const TICK_AT_TARGET = TICK_MIN + (100 / TICK_CAP) * TICK_SPAN;

/** DD/MM/YY-DD/MM/YY. Both ends carry the year — no "only on the newest" rule. */
function weekRangeLabel(p) {
  return formatIsoRangeShort(p.from, p.to);
}

/** A period's bare label, in whichever cadence this restaurant actually targets by. */
function periodLabel(p, period) {
  return period === 'month' ? periodKeyLabel(p.period_key, 'month') : weekRangeLabel(p);
}

// The same label as a HEADING: a week range needs "השבוע של" in front of it to
// read as a sentence, while a month name already is one ("החודש של ספטמבר 2026"
// reads as a stumble). Used wherever a period titles something — the card
// subtitle and every block in the שאר ה… tab — so the two never drift apart.
function PeriodHeading({ p, period }) {
  if (period === 'month') return periodKeyLabel(p.period_key, 'month');
  return <>השבוע של <span dir="ltr">{weekRangeLabel(p)}</span></>;
}

export default function WeeklyBudgetBreakdown({ dateFrom, dateTo }) {
  const { canEdit } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  // A card-level tab, not an expander: two earlier tries failed here. Per
  // category expanders (one each) read as clutter; a single green text link
  // above the categories read as a caption and promised to change something
  // a screen below it, so nobody found it. The house .view-tabs bar can only
  // be read as a control, and what it swaps sits directly under it.
  const [tab, setTab] = useState('current');
  // History is grouped by PERIOD, and deep ranges run to 13 of them — paged
  // so the first paint stays one scroll, never a wall.
  const [shownPeriods, setShownPeriods] = useState(PERIOD_PAGE);
  const [menuOpen, setMenuOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const bodyRef = useRef(null);
  // Desktop hover readout on the current tab's rows — carried over from the
  // retired רכש מול יעדי תקציב card, which had one. 640 is the same
  // content-density breakpoint that card and ClassificationAnalysis use; a
  // phone has no hover, and gets the same figures from the row itself.
  const isMobile = useIsMobile(640);
  const rowsRef = useRef(null);
  const { tip, show: showTip, hide: hideTip } = useHoverTip(rowsRef);

  const load = useCallback(() => {
    if (!dateFrom || !dateTo) { setData(null); setFailed(false); setLoading(false); return Promise.resolve(); }
    setLoading(true);
    return categoryLinesApi.weeklyBreakdown({ date_from: dateFrom, date_to: dateTo })
      .then(r => { setData(r.data); setFailed(false); })
      // A 403 (budget module off) lands here too. AnalysisPage never mounts
      // the card in that case, so this only happens when the flag flips while
      // the page is open, and the failure line covers it.
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  // A stale tab (or page depth) must not survive a period change.
  // A tip must not outlive the row it points at either: a refetch would
  // otherwise read one category's figures out under another's name.
  useEffect(() => { setTab('current'); setShownPeriods(PERIOD_PAGE); hideTip(); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching tabs swaps the whole card body, so a card whose top is already
  // scrolled past would otherwise dump the user into the middle of the new
  // content. Optional call: jsdom has no scrollIntoView at all.
  const openTab = (next) => {
    setTab(next);
    hideTip();
    bodyRef.current?.scrollIntoView?.({ block: 'start' });
  };

  // Same noun the card's own copy uses elsewhere, safe before data arrives —
  // periodNoun(undefined) falls back to the month set.
  const headerUnit = periodNoun(data?.period);

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 7 }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink-muted)', margin: 0 }}>
        פירוט {headerUnit.adjective} מול יעדי תקציב
      </h3>
      {/* Same page-menu chrome as הכנסות מול הוצאות directly above this card,
          so the two read as one product. */}
      <div className="page-menu" style={{ position: 'relative' }}>
        <button
          className="page-menu-trigger"
          aria-label="פעולות נוספות"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >⋮</button>
        {menuOpen && (
          <>
            <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="page-menu-panel" style={{ minWidth: 300 }}>
              {canEdit && (
                <Link
                  className="page-menu-item"
                  // This card is ABOUT targets, so the link jumps straight to
                  // the targets sheet with everything else collapsed (see
                  // CategoriesManagementPage's arrivedForTargets). The page
                  // menu's own "ניהול קטגוריות" opens the plain page.
                  to="/categories-management?targets=1"
                  onClick={() => setMenuOpen(false)}
                  title="הגדרת קטגוריות, יעדים ושיוכים"
                >
                  <Icon name="coins" size={15} /> ניהול יעדי תקציב
                </Link>
              )}
              {/* Two sentences and a worked example, in round numbers — the same
                  shape הכנסות מול הוצאות's note uses (utils/cardMenu.js), because
                  a help text that restates the screen is one nobody finishes.
                  Everything the card already says where it happens is CUT, and
                  the coined labels move to the glossary below rather than being
                  described in prose here.

                  SELF-CONTAINED: no mention of רכש מול יעדי תקציב, which this
                  card replaced. */}
              <div style={{ ...MENU_SECTION, margin: '6px 0 0' }}>
                <div style={MENU_HEADING}>איך מחושב</div>
                <p style={NOTE}>
                  כל {headerUnit.unit} נמדד בנפרד, מול היעד של אותה קטגוריה
                  {' '}ל{headerUnit.unit} - ולא כממוצע אחד לכל הטווח. יעד של
                  {' '}<b>9,000 ₪</b> ורכש של <b>4,500 ₪</b> מציגים <b>50%</b>.
                </p>
                <p style={{ ...NOTE, marginTop: 8 }}>
                  נספרים רק מסמכים <b>מאושרים</b>, וכל הסכומים <b>ללא מע״מ</b>.
                </p>
              </div>

              {/* The card's coined labels, defined where there is room. Each one
                  is shown as it appears on the card, because that is how the
                  reader got here. */}
              <div style={MENU_SECTION}>
                <div style={MENU_HEADING}>חשוב לדעת</div>

                <div style={CHIP_ROW}><span style={CHIP}>שאר ה{headerUnit.plural}</span></div>
                <p style={NOTE}>
                  שאר ה{headerUnit.plural} שבטווח שבחרתם - לא ארכיון. הפס הקטן
                  {' '}מראה אותם בקיצור: עמודה לכל {headerUnit.unit}, ו<b>הקו האופקי
                  {' '}הוא גובה היעד</b> - עמודה שעוברת אותו היא חריגה.
                </p>

                <div style={{ ...CHIP_ROW, marginTop: 12 }}><span style={CHIP}>אין יעד מוגדר</span></div>
                <p style={NOTE}>
                  לא נקבע יעד לקטגוריה ב{headerUnit.unit} הזה, ולכן אין מול מה למדוד
                  {' '}אותה. הרכש עצמו עדיין מוצג.
                </p>

                <div style={{ ...CHIP_ROW, marginTop: 12 }}><span style={CHIP}>ללא שיוך</span></div>
                <p style={NOTE}>
                  רכש שלא שויך לאף קטגוריה. הוא אינו נמדד מול יעד, ומוצג עם חלקו
                  {' '}מתוך הרכש של אותו {headerUnit.unit}.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="card">
        {header}
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      </div>
    );
  }

  if (!dateFrom || !dateTo) {
    return (
      <div className="card">
        {header}
        <div style={{ color: '#94a3b8', fontSize: 13.5 }}>
          בחרו טווח תאריכים כדי לראות פירוט מול יעד לפי תקופה.
        </div>
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="card">
        {header}
        <div style={{ color: '#94a3b8', fontSize: 13.5 }}>לא ניתן לטעון את הפירוט כרגע.</div>
      </div>
    );
  }

  const { period, lines, unmapped = [] } = data;

  // A range with only one period still gets the bold latest-period row below —
  // picking "September" should show September, not an empty card. The
  // "{unit} קודמים" history section is what has nothing to show in that case,
  // and it already hides itself below (history.length > 0).
  if (lines.length === 0) return null;

  // A range that starts after today. `computeWeeklyBreakdownForRange` drops
  // every bucket whose start is in the future, so this arrives as lines that
  // exist but carry NO periods at all — and the date picker reaches it in one
  // click (pick next month). Without this branch the never-budgeted nudge
  // below fires, because "no period has a target" is vacuously true of an
  // empty list: a restaurant with perfectly good targets was told to go set
  // targets. Same sentence and same treatment הכנסות מול הוצאות already uses
  // for its own `countableDays === 0`, since it is the same fact.
  if (lines.every((l) => l.periods.length === 0)) {
    return (
      <div className="card">
        {header}
        <div style={{ color: '#94a3b8', fontSize: 13.5 }}>הטווח שנבחר בעתיד - עדיין אין ימים למדוד.</div>
      </div>
    );
  }

  const unit = periodNoun(period);
  const everBudgeted = lines.some((l) => l.periods.some((p) => p.has_target));

  if (!everBudgeted) {
    // Never-budgeted state — ranked by spend, no target bars, one nudge. Kept
    // from the retired רכש מול יעדי תקציב card's own wording.
    const ranked = [...lines]
      .map((l) => ({ ...l, spend: l.periods.reduce((n, p) => n + p.actual_net, 0) }))
      .sort((a, b) => b.spend - a.spend);
    const maxSpend = Math.max(...ranked.map((l) => l.spend), 0);
    const rangeUnmappedNet = unmapped.reduce((n, u) => n + (u.actual_net || 0), 0);
    const barPct = (v) => (maxSpend > 0 ? Math.max(0, (v / maxSpend) * 100) : 0);
    return (
      <div className="card">
        {header}
        {ranked.map((l, i) => (
          <div key={l.category_line_id} style={{
            display: 'grid', gridTemplateColumns: 'minmax(72px,108px) 1fr auto',
            gap: 10, alignItems: 'center', fontSize: 12.5, padding: '5px 0',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ ...ROW_DOT, background: categoryLineColor(l, i) }} />
              <span style={CAT_NAME} title={l.category_line_name}>{l.category_line_name}</span>
            </span>
            <span style={{ background: TRACK_BG, borderRadius: 4, height: 15, overflow: 'hidden' }}>
              <span style={{ display: 'block', height: '100%', borderRadius: 4, background: categoryLineColor(l, i), width: `${barPct(l.spend)}%` }} />
            </span>
            <span style={{ color: 'var(--ink-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {formatCurrencyWhole(l.spend)}
            </span>
          </div>
        ))}
        {/* Spend no mapping rule reaches, closing the list without a bar — it
            is not a category, and drawing it as one would rank "nowhere"
            against the places the money actually went. Kept from the retired
            רכש מול יעדי תקציב card, which reported it in this same state;
            without it a restaurant mid-setup (module on, targets not set yet —
            exactly when unmapped spend is largest) sees no sign of it here at
            all. Summed over the WHOLE range, unlike the graded state's
            per-period figure, because every row beside it is a range total.
            No amber callout: this state already spends its one call to action
            on "קבע יעדי תקציב" below, and a second one would compete with it. */}
        {rangeUnmappedNet > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(72px,108px) 1fr auto',
            gap: 10, alignItems: 'center', fontSize: 12.5, marginTop: 6, paddingTop: 8,
            borderTop: '1px solid #eef1f5', color: 'var(--ink-muted)',
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>ללא שיוך</span>
            <span />
            <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {formatCurrencyWhole(rangeUnmappedNet)}
            </span>
          </div>
        )}
        {canEdit ? (
          <Link to="/categories-management?targets=1" style={{
            display: 'block', marginTop: 12, marginInline: -12, padding: '9px 12px', borderRadius: 8,
            background: 'rgba(202, 138, 4, 0.10)', fontSize: 12.5, lineHeight: 1.6, color: '#6b4f14', textDecoration: 'none',
          }}>
            על מנת לראות פירוט לפי {unit.unit}, {' '}
            <span style={{ color: '#0f3460', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}>קבע יעדי תקציב</span>
          </Link>
        ) : (
          <div style={{
            marginTop: 12, marginInline: -12, padding: '9px 12px', borderRadius: 8,
            background: 'rgba(202, 138, 4, 0.10)', fontSize: 12.5, lineHeight: 1.6, color: '#6b4f14',
          }}>
            על מנת לראות פירוט לפי {unit.unit},{' '}
            <span style={{ fontWeight: 600, color: '#475569' }}>קבע יעדי תקציב</span>
          </div>
        )}
      </div>
    );
  }

  const latestPeriod = lines[0].periods[lines[0].periods.length - 1];
  const subtitle = <PeriodHeading p={latestPeriod} period={period} />;
  // Every line is bucketed over the SAME periods (computeWeeklyBreakdownForRange
  // maps one bucket list across all of them), so lines[0] is the canonical
  // period axis and index i means the same period in every line — which is
  // what lets the "שאר ה..." tab pivot the table from category-major to
  // period-major without re-asking the server for anything.
  const periodAxis = lines[0].periods;
  const priorCount = periodAxis.length - 1;
  const anyHistory = priorCount > 0;
  // A finished period only ever carries a verdict (met/over), never the
  // amber "pace" status — same fact the old icon-strip's binary choice
  // encoded, just spent here on a bar's height instead of an icon shape.
  // The scale is shared with the budget line drawn across the strip: a tick
  // runs TICK_MIN at 0% and TICK_MIN+TICK_SPAN at TICK_CAP, so 100% lands at
  // TICK_AT_TARGET and anything over the target visibly clears the line.
  const activeLine = tip ? lines[tip.i] : null;
  // Graded only: a refetch can swap the row under a still-open tip for one
  // with no target, and the tooltip must not render "undefined%" for the frame
  // before the [data] effect clears it.
  const tipLatest = activeLine ? activeLine.periods[activeLine.periods.length - 1] : null;
  const activeLatest = tipLatest && tipLatest.has_target && tipLatest.pct_of_target != null ? tipLatest : null;
  const tickHeight = (p) => (!p.has_target ? TICK_MIN : TICK_MIN + (Math.min(TICK_CAP, p.pct_of_target) / TICK_CAP) * TICK_SPAN);
  const tickColor = (p) => (!p.has_target ? '#c2c8d1' : (p.status === 'over' ? STATUS_COLOR.over : STATUS_COLOR.on_track));
  const seam = (i) => (i === 0 ? {} : { borderTop: '1px solid #eef1f5', paddingTop: 20, marginTop: 4 });

  // Spend no mapping rule reaches. It is not a category and gets no bar, but
  // without it this card's percentages can describe a minority of the money in
  // silence. Stated PER PERIOD, because the two tabs show different windows —
  // on this data the latest week is 60.2% unmapped while the whole picked range
  // is 55.9%, so one range-wide figure would be wrong in both tabs.
  //
  // In the LATEST period an editor gets the amber "שייך לקטגוריות" callout
  // instead of the quiet line. It came over from the retired רכש מול יעדי
  // תקציב card, and it covers exactly the dates the first tab shows (owner),
  // not the whole range. The "שאר ה..." blocks keep the quiet line: one amber
  // callout per period block would bury the data it is annotating. A viewer
  // can't map anything, so they get the quiet line everywhere.
  const unmappedAt = (idx) => {
    const u = unmapped[idx];
    return u && u.actual_net > 0 ? u : null;
  };
  const UnmappedLine = ({ at, grid }) => {
    const u = unmappedAt(at);
    if (!u) return null;
    // Label and figure only, in BOTH tabs. The "not measured against a target"
    // part is said once in the ⋮ note; repeating it on every line it applies to
    // read as redundant (owner).
    const figure = `${formatCurrencyWhole(u.actual_net)} (${u.pct_of_spend}%)`;
    return grid ? (
      <div style={{ ...HIST_GRID, padding: '5px 0' }}>
        <span style={{ fontSize: 13, color: MUTED }}>ללא שיוך</span>
        <span style={{ fontSize: 13, color: MUTED, textAlign: 'right' }}>{figure}</span>
      </div>
    ) : (
      <div style={{ borderTop: '1px solid #eef1f5', paddingTop: 12, marginTop: 16, fontSize: 13, color: MUTED }}>
        ללא שיוך · {figure}
      </div>
    );
  };

  return (
    <div className="card">
      {header}
      {/* With tabs, the LATEST tab states the period, so the old subtitle line
          would just repeat it under the title. Without tabs there is nothing
          else naming the period, so it stays. */}
      {!anyHistory && (
        <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 6 }}>{subtitle}</div>
      )}

      {/* The house tab bar (.view-tabs/.view-tab — slim underline, folder tabs
          on desktop, full-width 48px targets on a phone), not a control of this
          card's own invention: a filled segmented pill already shipped here and
          across /suppliers and /credits once, and was dropped precisely because
          two halves of the app then disagreed about what a tab looks like. Same
          aria-pressed buttons RevenueVsExpensesReportPage uses — this switches a
          panel in place and changes no URL, and role="tab" would owe the reader
          arrow-key navigation none of these bars implement. The bar's own
          `margin: -24px -24px 18px` bleeds it to the top of a card whose FIRST
          child it is; here it sits under the title, so only the block margins
          are overridden and the horizontal bleed is left to the class (and to
          the ≤720 block, which zeroes it against .card's smaller padding).
          The count rides on the tab so the card states that N other periods
          exist before anyone has to guess that they do. */}
      {anyHistory && (
        <nav
          className="view-tabs"
          aria-label="תצוגת הפירוט"
          ref={bodyRef}
          style={{ marginTop: 0, marginBottom: 14 }}
        >
          {[
            // The bare range, not the "השבוע של" heading: a tab is a control
            // label with half a phone's width to live in, and the prefix would
            // push a DD/MM/YY-DD/MM/YY range past it.
            { key: 'current', label: <span dir={period === 'week' ? 'ltr' : undefined}>{periodLabel(latestPeriod, period)}</span> },
            { key: 'history', label: `שאר ה${unit.plural} (${priorCount})` },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className={`view-tab${tab === t.key ? ' is-active' : ''}`}
              aria-pressed={tab === t.key}
              onClick={() => openTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>
      )}

      {/* Capped, unlike the נוכחי panel: there every row has a full-width
          progress bar tying the name to its percentage, so the two may sit at
          opposite edges. Here there is no bar, and on a wide card a figure
          stranded 600px from its label reads as unrelated — the same
          complaint that killed the first expanded-list design. */}
      {tab === 'history' && (
        <div data-panel="rest" style={{ display: 'flex', flexDirection: 'column', maxWidth: 460 }}>
          {periodAxis.slice(0, -1).reverse().slice(0, shownPeriods).map((axisPeriod, blockIdx) => {
            // reverse() above counts back from the newest prior period, so
            // index 0 is "last week" — the row people actually came for.
            const idx = priorCount - 1 - blockIdx;
            return (
              <div
                key={axisPeriod.period_key}
                style={{
                  padding: '14px 0 8px',
                  ...(blockIdx === 0 ? {} : { borderTop: '1px solid #eef1f5' }),
                }}
              >
                {/* Heading only — no period total beside it. Every category's
                    own רכש is already one line below, and a sum across
                    categories is a different question from the one this card
                    asks (it is the card ABOVE that totals a range). */}
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
                  <PeriodHeading p={axisPeriod} period={period} />
                </div>
                {lines.map((l, i) => {
                  const p = l.periods[idx];
                  return (
                    <div key={l.category_line_id} style={{ ...HIST_GRID, padding: '5px 0' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span style={{ ...ROW_DOT, width: 8, height: 8, background: categoryLineColor(l, i) }} />
                        <span style={{ ...CAT_NAME, fontSize: 13 }}>{l.category_line_name}</span>
                      </span>
                      <span style={{ fontSize: 13, textAlign: 'right' }}>
                        {p.has_target ? (
                          <>
                            <b style={{ fontWeight: 700, color: STATUS_TEXT_COLOR[p.status] || STATUS_COLOR[p.status] }}>
                              {Math.round(p.pct_of_target)}%
                            </b>{' '}
                            <span style={{ color: '#64748b', fontWeight: 500 }}>
                              · רכש {formatCurrencyWhole(p.actual_net)} {EX_VAT} · יעד {formatCurrencyWhole(p.target_net)}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: '#64748b', fontWeight: 500 }}>
                            אין יעד · רכש {formatCurrencyWhole(p.actual_net)} {EX_VAT}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
                <UnmappedLine at={idx} grid />
              </div>
            );
          })}

          {/* Card-level, like every other control here — never one per category. */}
          {priorCount > shownPeriods && (
            <button
              type="button"
              onClick={() => setShownPeriods((n) => n + PERIOD_PAGE)}
              style={{
                all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', minHeight: 44, marginTop: 12,
                background: '#f7faf8', border: '1px solid #eef1f5', borderRadius: 8,
                fontSize: 13, fontWeight: 600, color: '#1a6e4d',
              }}
            >
              הצג עוד {Math.min(PERIOD_PAGE, priorCount - shownPeriods)} {headerUnit.plural}
            </button>
          )}
        </div>
      )}

      <div ref={rowsRef} style={{ display: tab === 'current' ? 'flex' : 'none', flexDirection: 'column', position: 'relative' }}>
        {lines.map((l, i) => {
          const periods = l.periods;
          const latest = periods[periods.length - 1];
          const history = periods.slice(0, -1);
          const lineEverBudgeted = periods.some((p) => p.has_target);
          const spend = periods.reduce((n, p) => n + p.actual_net, 0);

          if (!lineEverBudgeted) {
            // Never budgeted, but a SIBLING line in this card is — the
            // card-level nudge above already says "go set a target", so this
            // row just states the fact rather than repeating that ask.
            return (
              <div key={l.category_line_id} style={seam(i)}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span className="budget-category-label" style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 0', minWidth: 0, marginInlineEnd: 8 }}>
                    <span style={{ ...ROW_DOT, background: categoryLineColor(l, i) }} />
                    <span style={CAT_NAME} title={l.category_line_name}>{l.category_line_name}</span>
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 500, color: MUTED, flexShrink: 0, whiteSpace: 'nowrap' }}>אין יעד</span>
                </div>
                <span style={NEUTRAL_BAR}>
                  <span style={{ display: 'block', height: '100%', width: '34%', background: '#dbe1e8', borderRadius: 4 }} />
                </span>
                <div style={{ fontSize: 13, color: CAPTION }}>
                  רכש {formatCurrencyWhole(spend)} {EX_VAT}
                </div>
              </div>
            );
          }

          // `graded`, not `has_target`: the server sets has_target from the
          // mere EXISTENCE of a target row, while barGeometry (and the
          // pct_of_target behind it) additionally require the prorated amount
          // to be > 0. The two disagree for a target so small that
          // `amount / bucketDays * daysInRange` rounds to 0.00 — at which point
          // pct_of_target is null, barGeometry returns null, and reading
          // .approvedWidth off it would blank the whole page. Treat that as
          // "no target for this period", which is what it effectively is.
          const latestGeo = latest.has_target ? barGeometry(latest) : null;
          const graded = latest.has_target && latestGeo != null;
          const latestPct = graded ? Math.round(latest.pct_of_target) : null;
          const barColor = graded ? STATUS_COLOR[latest.status] : null;
          const textColor = graded ? (STATUS_TEXT_COLOR[latest.status] || STATUS_COLOR[latest.status]) : MUTED;

          // Hover covers name, bar and caption — not the trend strip below,
          // which is its own control (it opens the other tab). Graded rows only,
          // like the retired card: a row with no target has nothing the row
          // itself does not already say.
          const hoverProps = graded && !isMobile
            ? { onMouseEnter: (e) => showTip(i, e.currentTarget), onMouseLeave: hideTip }
            : {};

          return (
            <div key={l.category_line_id} style={seam(i)}>
              <div {...hoverProps}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                <span className="budget-category-label" style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 0', minWidth: 0, marginInlineEnd: 8 }}>
                  <span style={{ ...ROW_DOT, background: categoryLineColor(l, i) }} />
                  <span style={CAT_NAME} title={l.category_line_name}>{l.category_line_name}</span>
                </span>
                {graded ? (
                  <span className="pct-figure" style={{ color: textColor, flexShrink: 0 }}>{latestPct}%</span>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 500, color: MUTED, flexShrink: 0, whiteSpace: 'nowrap' }}>אין יעד</span>
                )}
              </div>

              {graded ? (
                <>
                  {/* barGeometry, not a local Math.min: this card once computed
                      its own widths and tick position and drew a different
                      geometry from every other target bar — most visibly past
                      200%, where the shared helper caps the scale and this one
                      put the target tick 3% from the edge. Over-target splits
                      at the target line: spend-to-target in the tint, the
                      overrun in solid red, so magnitude is legible instead of a
                      uniform red block. */}
                  <span style={{ display: 'block', position: 'relative', background: TRACK_BG, borderRadius: 4, height: 15, overflow: 'hidden', marginBottom: 6 }}>
                    <span style={{
                      display: 'block', height: '100%', width: `${latestGeo.approvedWidth}%`,
                      background: barColor, borderRadius: 4,
                    }} />
                    {/* Where the budget ran out. Absent until spend passes it,
                        so "no marker" is itself the good news — a second,
                        non-colour cue that the bar has gone over. */}
                    {latestGeo.targetAt < 100 && (
                      <span style={{ position: 'absolute', top: 0, bottom: 0, right: `${latestGeo.targetAt}%`, width: 2, background: '#1b2621' }} />
                    )}
                  </span>
                  <div style={{ fontSize: 13, color: CAPTION, marginBottom: 12 }}>
                    רכש {formatCurrencyWhole(latest.actual_net)} {EX_VAT} · יעד {formatCurrencyWhole(latest.target_net)}
                  </div>
                </>
              ) : (
                <>
                  <span style={NEUTRAL_BAR}>
                    <span style={{ display: 'block', height: '100%', width: '34%', background: '#dbe1e8', borderRadius: 4 }} />
                  </span>
                  <div style={{ fontSize: 13, color: CAPTION, marginBottom: 12 }}>
                    רכש {formatCurrencyWhole(latest.actual_net)} {EX_VAT}
                  </div>
                </>
              )}
              </div>

              {/* A silent second door into the "שאר ה..." tab: the strip IS the
                  thing that tab explains, so poking it should go there. No
                  chevron and no per-category state — adding either back is
                  what made this card read as cluttered the first time. */}
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={() => openTab('history')}
                  aria-label={`הצג את שאר ה${unit.plural} - ${l.category_line_name}`}
                  style={{
                    all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', gap: 6, minHeight: 44, width: '100%',
                  }}
                >
                  {/* Names the tab it opens, rather than describing itself —
                      the strip's whole job is to be the second way in. */}
                  <span style={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap' }}>שאר ה{unit.plural}</span>
                  <span style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: 3, height: STRIP_H }}>
                    {/* The budget line, drawn across the strip at the height a
                        tick reaches at exactly 100%. Without it this strip was
                        the card's one COLOUR-ONLY signal: a period that ended
                        at 95% and one that ended at 105% are near-identical
                        heights, so green-vs-red was the only thing separating
                        "made it" from "blew it" — invisible to roughly one man
                        in twelve. Now a period that went over pokes ABOVE the
                        line, which is a difference in shape, not hue. */}
                    <span
                      aria-hidden="true"
                      style={{
                        position: 'absolute', insetInlineStart: 0, insetInlineEnd: 0,
                        bottom: TICK_AT_TARGET, height: 1, background: '#cbd5e1',
                      }}
                    />
                    {history.map((p) => (
                      <span
                        key={p.period_key}
                        style={{ position: 'relative', width: 5, borderRadius: 2, height: tickHeight(p), background: tickColor(p) }}
                      />
                    ))}
                  </span>
                </button>
              )}
            </div>
          );
        })}
        {activeLatest && (
          <ChartTooltip
            tip={tip}
            title={activeLine.category_line_name}
            // Same rule as the retired card: no pace word once the period is
            // over, because there is nothing left to predict.
            titleNote={activeLatest.range_ended ? null : statusLabel(activeLatest.status)}
            // Exact to the agora — the row rounds because a scannable list
            // should, but someone who stopped to hover wants the real number.
            // The % is the server's one-decimal figure (73.3%), where the row
            // rounds to a whole one (73%) — same exact-on-hover rule.
            rows={[
              { label: 'בפועל', value: `${formatCurrency(activeLatest.actual_net)} ${EX_VAT}` },
              { label: 'יעד', value: formatCurrency(activeLatest.target_net) },
              { label: 'מהיעד', value: `${activeLatest.pct_of_target}%` },
            ]}
          />
        )}
        {canEdit && unmappedAt(periodAxis.length - 1) ? (
          <AmberCallout actionLabel="שייך לקטגוריות" actionTo="/categories-management">
            <strong>{formatCurrencyWhole(unmappedAt(periodAxis.length - 1).actual_net)}</strong>
            {' '}({unmappedAt(periodAxis.length - 1).pct_of_spend}%) רכש לא משויך
          </AmberCallout>
        ) : (
          <UnmappedLine at={periodAxis.length - 1} />
        )}
      </div>

      {/* לצפייה: no report exists yet, so the click's whole purpose is the
          event it fires — the owner measures demand before building one.
          Tracked on the click, not inside the sheet, so a re-render of an open
          sheet cannot inflate the number this exists to produce. Same event
          the retired רכש מול יעדי תקציב card fired (source
          'target_card_footer'); this card's own source keeps the two apart
          in the numbers. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        width: '100%', minHeight: 44, marginTop: 12, padding: '6px 4px 6px 0',
        borderTop: '1px solid #eef1f5',
      }}>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          onClick={() => {
            track(EVENTS.BUDGET_REPORT_INTEREST, { source: 'weekly_breakdown_card_footer' });
            setComingSoonOpen(true);
          }}
        >
          לצפייה <Icon name="chevron" size={15} style={{ transform: 'rotate(90deg)' }} />
        </button>
      </div>

      <ComingSoonSheet
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        title="יש למה לצפות?"
      >
        בקרוב יהיה כאן דוח מפורט.
        <br />
        לפרטים פנו אלינו ב-Peak <Icon name="heart" size={14} style={{ verticalAlign: '-2px' }} />
      </ComingSoonSheet>
    </div>
  );
}
